import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import cn from 'classnames';
import {
  ChevronLeft,
  ChevronRight,
  PlayArrow,
  Pause,
  Fullscreen,
  FullscreenExit,
} from '@material-ui/icons';
import AlterImage from 'Assets/alter-image.png';
import styles from './SharedAlbumPlayer.module.css';

// Purpose-built public album viewer: a single-view slideshow with a centered
// photo (object-fit: contain), prev/play/next controls on one row, a bottom
// thumbnail carousel, fullscreen, keyboard control, and auto-hiding controls.
const SharedAlbumPlayer = ({ item }) => {
  const photos = useMemo(() => {
    const list = Array.isArray(item?.photos) ? [...item.photos] : [];
    list.sort((a, b) => (Number(a?.order_number) || 0) - (Number(b?.order_number) || 0));
    return list.map((p) => p?.imagekit_url || p?.photo_file || AlterImage);
  }, [item]);

  const count = photos.length;
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(count > 1);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const containerRef = useRef(null);
  const hideTimer = useRef(null);
  const autoTimer = useRef(null);

  const go = useCallback((dir) => {
    setIndex((i) => (i + dir + count) % count);
  }, [count]);

  // Auto-advance while playing.
  useEffect(() => {
    if (!playing || count < 2) return undefined;
    autoTimer.current = setInterval(() => setIndex((i) => (i + 1) % count), 5000);
    return () => clearInterval(autoTimer.current);
  }, [playing, count]);

  // Show controls, then hide after a short idle period.
  const pingControls = useCallback(() => {
    setControlsVisible(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setControlsVisible(false), 2600);
  }, []);

  useEffect(() => {
    pingControls();
    return () => clearTimeout(hideTimer.current);
  }, [pingControls]);

  // Keyboard: ←/→ navigate, space toggles play/pause.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') { go(-1); pingControls(); } else if (e.key === 'ArrowRight') { go(1); pingControls(); } else if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        if (count > 1) setPlaying((p) => !p);
        pingControls();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, pingControls, count]);

  // Track native fullscreen state.
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  if (!count) {
    return <div className={styles.empty}>This album has no photos.</div>;
  }

  return (
    <div
      ref={containerRef}
      className={cn(styles.viewer, { [styles.hidden]: !controlsVisible })}
      onMouseMove={pingControls}
      onClick={pingControls}
      role="presentation"
    >
      <div className={styles.stage}>
        {photos.map((src, i) => (
          <img
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            src={src}
            alt=""
            className={cn(styles.photo, { [styles.photoActive]: i === index })}
            onError={(e) => { e.currentTarget.src = AlterImage; }}
          />
        ))}

        {count > 1 && (
          <>
            <button
              type="button"
              className={cn(styles.navBtn, styles.navLeft, styles.control)}
              onClick={() => go(-1)}
              aria-label="Previous photo"
            >
              <ChevronLeft />
            </button>
            <button
              type="button"
              className={cn(styles.playBtn, styles.control)}
              onClick={() => setPlaying((p) => !p)}
              aria-label={playing ? 'Pause' : 'Play'}
            >
              {playing ? <Pause /> : <PlayArrow />}
            </button>
            <button
              type="button"
              className={cn(styles.navBtn, styles.navRight, styles.control)}
              onClick={() => go(1)}
              aria-label="Next photo"
            >
              <ChevronRight />
            </button>
          </>
        )}

        <button
          type="button"
          className={cn(styles.fsBtn, styles.control)}
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
        </button>
      </div>

      {count > 1 && (
        <div className={cn(styles.thumbs, styles.control)}>
          <div className={styles.thumbsInner}>
            {photos.map((src, i) => (
              <button
                type="button"
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className={cn(styles.thumb, { [styles.thumbActive]: i === index })}
                onClick={() => setIndex(i)}
                aria-label={`Go to photo ${i + 1}`}
              >
                <img
                  src={src}
                  alt=""
                  onError={(e) => { e.currentTarget.src = AlterImage; }}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedAlbumPlayer;
